import { FormEvent, useState } from "react";
import { Bug } from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitInput } from "@/app/routes/dashboard/components/kit/Input";
import { KitTextarea } from "@/app/routes/dashboard/components/kit/Textarea";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitSegmentedControl } from "@/app/routes/dashboard/components/kit/SegmentedControl";
import { KitFieldMessage, kitLabelClass } from "@/app/routes/dashboard/components/kit/Field";
import {
  useSubmitFeedback,
  type FeedbackType,
} from "@/app/routes/dashboard/api/useFeedback";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

const TYPES: { value: FeedbackType; label: string; hint: string }[] = [
  { value: "Bug", label: "Bug", hint: "Something broken or wrong" },
  { value: "Recommendation", label: "Recommendation", hint: "Idea or improvement" },
];

export default function FeedbackModal({ isOpen, onOpenChange }: Props) {
  const submit = useSubmitFeedback();
  const [type, setType] = useState<FeedbackType>("Bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = () => {
    setType("Bug");
    setTitle("");
    setDescription("");
    setError(null);
    setDone(false);
    submit.reset();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await submit.mutateAsync({ type, title: title.trim(), description: description.trim() });
      setDone(true);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "Could not send. Please try again.";
      setError(msg);
    }
  };

  return (
    <KitModal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      title={done ? "Thanks" : "Report a bug / Recommendation"}
      description={
        done
          ? "We got it. The team will review your note."
          : "Tell us what broke or what you’d like to see. We read every submission."
      }
      icon={Bug}
    >
      {done ? (
        <div className="flex justify-end pt-2">
          <KitButton type="button" onClick={() => handleOpenChange(false)}>
            Close
          </KitButton>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <span className={kitLabelClass}>Type</span>
            <KitSegmentedControl
              ariaLabel="Type"
              options={TYPES.map((t) => ({ label: t.label, value: t.value }))}
              value={type}
              onChange={setType}
            />
            <span className="text-[12.5px] leading-[1.45] text-co-faint">
              {TYPES.find((t) => t.value === type)?.hint}
            </span>
          </div>

          <KitInput
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={type === "Bug" ? "Brief summary of the issue" : "What should we improve?"}
            required
            maxLength={120}
          />

          <KitTextarea
            label="Details"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            minLength={10}
            maxLength={4000}
            rows={5}
            placeholder={
              type === "Bug"
                ? "What happened, what you expected, and how to reproduce it…"
                : "Describe the idea and why it would help…"
            }
          />

          {error ? <KitFieldMessage>{error}</KitFieldMessage> : null}

          <div className="flex justify-end gap-2 pt-1">
            <KitButton type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
              Cancel
            </KitButton>
            <KitButton type="submit" loading={submit.isPending}>
              Send
            </KitButton>
          </div>
        </form>
      )}
    </KitModal>
  );
}
